/**
 * Catalog (colors / wood_types / veneers) API client (Phase 3b Step 6/6b).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 */

import type { AttributeKind, CatalogInput, CatalogItem, CatalogSopFile } from './types'

export type CatalogApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class CatalogApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'CatalogApiClientError'
    this.code = code
    this.status = status
  }
}

type CatalogApiDto = {
  id: string
  code: string
  name: string
  swatch_hex?: string | null
  note?: string | null
  type?: string | null
  signed_date?: string | null
  sop_files?: Array<{
    id: string
    name: string
    dataUrl: string
    uploadedAt: string
    size?: number
  }>
  is_active: boolean
  created_at: string
  updated_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as CatalogApiError
  throw new CatalogApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

/**
 * Map catalog DTO → UI CatalogItem.
 */
export function catalogApiDtoToUi(dto: CatalogApiDto): CatalogItem {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    swatchHex: dto.swatch_hex ?? null,
    isActive: dto.is_active,
    note: dto.note ?? '',
    type: dto.type ?? null,
    signedDate: dto.signed_date ?? null,
    sopFiles: (dto.sop_files ?? []).map((f) => ({
      id: f.id,
      name: f.name,
      dataUrl: f.dataUrl,
      uploadedAt: f.uploadedAt,
      size: f.size,
    })),
  }
}

function listPath(kind: AttributeKind, includeInactive: boolean): string {
  const base =
    kind === 'color'
      ? '/api/catalog/colors'
      : kind === 'wood'
        ? '/api/catalog/wood-types'
        : '/api/catalog/veneers'
  return includeInactive ? `${base}?includeInactive=true` : base
}

function writePath(kind: AttributeKind, id?: string): string {
  const base =
    kind === 'color'
      ? '/api/catalog/colors'
      : kind === 'wood'
        ? '/api/catalog/wood-types'
        : '/api/catalog/veneers'
  return id ? `${base}/${encodeURIComponent(id)}` : base
}

function inputToBody(input: CatalogInput | Partial<CatalogInput>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (input.code !== undefined) body.code = input.code
  if (input.name !== undefined) body.name = input.name
  if (input.swatchHex !== undefined) body.swatch_hex = input.swatchHex
  if (input.isActive !== undefined) body.is_active = input.isActive
  if (input.note !== undefined) body.note = input.note
  if (input.type !== undefined) body.type = input.type
  if (input.signedDate !== undefined) body.signed_date = input.signedDate
  if (input.sopFiles !== undefined) body.sopFiles = input.sopFiles
  return body
}

export async function listColorsApi(
  includeInactive = false,
): Promise<CatalogItem[]> {
  return listCatalogApi('color', includeInactive)
}

export async function listWoodTypesApi(
  includeInactive = false,
): Promise<CatalogItem[]> {
  return listCatalogApi('wood', includeInactive)
}

export async function listVeneersApi(
  includeInactive = false,
): Promise<CatalogItem[]> {
  return listCatalogApi('veneer', includeInactive)
}

export async function listCatalogApi(
  kind: AttributeKind,
  includeInactive = false,
): Promise<CatalogItem[]> {
  const res = await fetch(listPath(kind, includeInactive))
  const body = await parseJson<
    | { ok: true; colors?: CatalogApiDto[]; wood_types?: CatalogApiDto[]; veneers?: CatalogApiDto[] }
    | CatalogApiError
  >(res)
  throwIfApiError(res, body, `Failed to list ${kind}`)
  const okBody = body as {
    ok: true
    colors?: CatalogApiDto[]
    wood_types?: CatalogApiDto[]
    veneers?: CatalogApiDto[]
  }
  const rows =
    kind === 'color'
      ? (okBody.colors ?? [])
      : kind === 'wood'
        ? (okBody.wood_types ?? [])
        : (okBody.veneers ?? [])
  return rows.map((dto) => catalogApiDtoToUi(dto))
}

export async function createCatalogApi(
  kind: AttributeKind,
  input: CatalogInput,
): Promise<CatalogItem> {
  const res = await fetch(writePath(kind), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inputToBody(input)),
  })
  const body = await parseJson<
    | {
        ok: true
        color?: CatalogApiDto
        wood_type?: CatalogApiDto
        veneer?: CatalogApiDto
      }
    | CatalogApiError
  >(res)
  throwIfApiError(res, body, `Tạo ${kind} thất bại`)
  const okBody = body as {
    ok: true
    color?: CatalogApiDto
    wood_type?: CatalogApiDto
    veneer?: CatalogApiDto
  }
  const dto =
    kind === 'color'
      ? okBody.color
      : kind === 'wood'
        ? okBody.wood_type
        : okBody.veneer
  if (!dto) {
    throw new CatalogApiClientError(
      'INVALID_RESPONSE',
      `Missing ${kind} in create response`,
      500,
    )
  }
  return catalogApiDtoToUi(dto)
}

export async function updateCatalogApi(
  kind: AttributeKind,
  id: string,
  input: Partial<CatalogInput>,
): Promise<CatalogItem> {
  const res = await fetch(writePath(kind, id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inputToBody(input)),
  })
  const body = await parseJson<
    | {
        ok: true
        color?: CatalogApiDto
        wood_type?: CatalogApiDto
        veneer?: CatalogApiDto
      }
    | CatalogApiError
  >(res)
  throwIfApiError(res, body, `Cập nhật ${kind} thất bại`)
  const okBody = body as {
    ok: true
    color?: CatalogApiDto
    wood_type?: CatalogApiDto
    veneer?: CatalogApiDto
  }
  const dto =
    kind === 'color'
      ? okBody.color
      : kind === 'wood'
        ? okBody.wood_type
        : okBody.veneer
  if (!dto) {
    throw new CatalogApiClientError(
      'INVALID_RESPONSE',
      `Missing ${kind} in update response`,
      500,
    )
  }
  return catalogApiDtoToUi(dto)
}

/** Soft-archive: PATCH is_active=false (no hard DELETE). */
export async function archiveCatalogApi(
  kind: AttributeKind,
  id: string,
): Promise<CatalogItem> {
  return updateCatalogApi(kind, id, { isActive: false })
}

export async function createColorApi(input: CatalogInput): Promise<CatalogItem> {
  return createCatalogApi('color', input)
}
export async function updateColorApi(
  id: string,
  input: Partial<CatalogInput>,
): Promise<CatalogItem> {
  return updateCatalogApi('color', id, input)
}
export async function createWoodTypeApi(
  input: CatalogInput,
): Promise<CatalogItem> {
  return createCatalogApi('wood', input)
}
export async function updateWoodTypeApi(
  id: string,
  input: Partial<CatalogInput>,
): Promise<CatalogItem> {
  return updateCatalogApi('wood', id, input)
}
export async function createVeneerApi(input: CatalogInput): Promise<CatalogItem> {
  return createCatalogApi('veneer', input)
}
export async function updateVeneerApi(
  id: string,
  input: Partial<CatalogInput>,
): Promise<CatalogItem> {
  return updateCatalogApi('veneer', id, input)
}

/** SOP file operations directly via API */
export async function listCatalogSopFilesApi(
  kind: AttributeKind,
  id: string,
): Promise<CatalogSopFile[]> {
  const base =
    kind === 'color'
      ? `/api/catalog/colors/${encodeURIComponent(id)}/sop-files`
      : kind === 'wood'
        ? `/api/catalog/wood-types/${encodeURIComponent(id)}/sop-files`
        : `/api/catalog/veneers/${encodeURIComponent(id)}/sop-files`
  const res = await fetch(base)
  const body = await parseJson<{ ok: true; files: CatalogSopFile[] } | CatalogApiError>(res)
  throwIfApiError(res, body, `Tải danh sách SOP của ${kind} thất bại`)
  return (body as { ok: true; files: CatalogSopFile[] }).files
}

export async function uploadCatalogSopFilesApi(
  kind: AttributeKind,
  id: string,
  files: File[],
): Promise<CatalogSopFile[]> {
  const base =
    kind === 'color'
      ? `/api/catalog/colors/${encodeURIComponent(id)}/sop-files/upload`
      : kind === 'wood'
        ? `/api/catalog/wood-types/${encodeURIComponent(id)}/sop-files/upload`
        : `/api/catalog/veneers/${encodeURIComponent(id)}/sop-files/upload`
  const formData = new FormData()
  for (const f of files) {
    formData.append('files', f)
  }
  const res = await fetch(base, {
    method: 'POST',
    body: formData,
  })
  const body = await parseJson<{ ok: true; files: CatalogSopFile[] } | CatalogApiError>(res)
  throwIfApiError(res, body, `Tải lên SOP cho ${kind} thất bại`)
  return (body as { ok: true; files: CatalogSopFile[] }).files
}

export async function deleteCatalogSopFileApi(
  kind: AttributeKind,
  id: string,
  fileId: string,
): Promise<void> {
  const base =
    kind === 'color'
      ? `/api/catalog/colors/${encodeURIComponent(id)}/sop-files/${encodeURIComponent(fileId)}`
      : kind === 'wood'
        ? `/api/catalog/wood-types/${encodeURIComponent(id)}/sop-files/${encodeURIComponent(fileId)}`
        : `/api/catalog/veneers/${encodeURIComponent(id)}/sop-files/${encodeURIComponent(fileId)}`
  const res = await fetch(base, { method: 'DELETE' })
  const body = await parseJson<{ ok: true } | CatalogApiError>(res)
  throwIfApiError(res, body, `Xóa SOP file thất bại`)
}
